/**
 * actionCta — role-aware CTA resolution for Action Center cards.
 *
 * The backend emits `cta_primary.href` pointing at listing pages
 * (/leads, /payments, /villas, /documents, /tasks) that are gated by
 * role in App.js. This helper rewrites hrefs when a safe alternative
 * exists (constructor → /villa/{villa_id}) and returns `null` when
 * navigation cannot be authorised (marketing/constructor hitting
 * admin-only routes). ActionCard consumes the return and, when null,
 * DOES NOT render a primary button — no disabled state, no fallback
 * redirect.
 *
 * Frozen scope for V1:
 *   admin       → verbatim
 *   constructor → construction cards with meta.villa_id ⇒ /villa/{id}
 *                 /tasks allowed; everything else hidden
 *   marketing   → /leads*, /leads/:id, /tasks allowed; everything else
 *                 hidden
 *   investor    → never reaches this codepath (route bloqueada) but
 *                 defensively returns null.
 */

/** Follow-up snooze presets used by ActionCard's kebab menu. */
export const FOLLOWUP_SNOOZE_PRESETS = [
  { key: "2h", label: "2 horas", labelEn: "2 hours" },
  { key: "tomorrow", label: "Mañana", labelEn: "Tomorrow" },
  { key: "3d", label: "3 días", labelEn: "3 days" },
  { key: "1w", label: "1 semana", labelEn: "1 week" },
];

/** Compute the target ISO for a given preset. Pure — no side effects. */
export function isoForSnoozePreset(presetKey, now = new Date()) {
  const dt = new Date(now.getTime());
  switch (presetKey) {
    case "2h":
      dt.setTime(dt.getTime() + 2 * 60 * 60 * 1000);
      return dt.toISOString();
    case "tomorrow": {
      dt.setDate(dt.getDate() + 1);
      dt.setHours(9, 0, 0, 0);
      return dt.toISOString();
    }
    case "3d":
      dt.setDate(dt.getDate() + 3);
      dt.setHours(9, 0, 0, 0);
      return dt.toISOString();
    case "1w":
      dt.setDate(dt.getDate() + 7);
      dt.setHours(9, 0, 0, 0);
      return dt.toISOString();
    default:
      return null;
  }
}

// Admin-only frontend routes today. Kept as a set so the intent is
// obvious and the check is O(1).
const ADMIN_ONLY_LISTS = new Set(["/villas", "/payments", "/documents"]);

function isLeadsHref(href) {
  return href === "/leads" || href.startsWith("/leads/") || href.startsWith("/leads?");
}
function isTasksHref(href) {
  return href === "/tasks" || href.startsWith("/tasks?");
}
function isVillasHref(href) {
  // Includes "/villas" and any "/villas?..." variant. Deep-links to
  // "/villa/:id" (singular) are handled separately — NOT admin-only.
  return href === "/villas" || href.startsWith("/villas?");
}

/**
 * Resolve the CTA a specific role is allowed to click.
 *
 * Returns:
 *   { label, href }  when the CTA is authorised.
 *   null             when the CTA must be hidden (button not rendered).
 *
 * Never returns a disabled button or a fallback redirect.
 */
export function resolvePrimaryCta(actionItem, role) {
  if (!actionItem || !actionItem.cta_primary) return null;
  const cta = actionItem.cta_primary;
  const href = cta.href || "";
  const label = cta.label || "Abrir";

  // Task auto-created for a lead (automations.lead_followup_task) carries
  // meta.lead_id verbatim from the task document. Admin/marketing get a
  // one-click jump to the lead detail; constructor and investor never
  // enter this branch and fall through to the standard role handling
  // (constructor still gets "Abrir tarea" → /tasks). Never inferred by
  // title/name — meta.lead_id is the only source of truth.
  const meta = actionItem.meta || {};
  if (
    actionItem.type === "task"
    && meta.lead_id
    && (role === "admin" || role === "marketing")
  ) {
    return {
      label: "Ver lead",
      href: `/leads/${encodeURIComponent(meta.lead_id)}`,
    };
  }

  // Admin sees everything verbatim.
  if (role === "admin") {
    return { label, href };
  }

  // Investor should never reach this codepath (route bloqueada at
  // /actions and no ActionCenter tab in InvestorDashboard). Defensive.
  if (role === "investor") {
    return null;
  }

  if (role === "constructor") {
    // Construction cards with meta.villa_id rewrite to VillaDetailPage —
    // keeping the backend href when it already targets that villa (it may
    // carry ?tab=progress for "Publicar avance").
    if (
      actionItem.type === "construction"
      && actionItem.meta
      && actionItem.meta.villa_id
    ) {
      const villaHref = `/villa/${encodeURIComponent(actionItem.meta.villa_id)}`;
      return {
        label,
        href: href.startsWith(villaHref) ? href : villaHref,
      };
    }
    // Tasks route accepts constructor.
    if (isTasksHref(href)) return { label, href };
    // Everything else — /villas, /payments, /documents, /leads* — is
    // gated by admin-only routes for constructor. Hide the CTA.
    return null;
  }

  if (role === "marketing") {
    // WhatsApp conversations are admin-only: promising "Abrir conversación"
    // would land marketing on a lead page without one. Same permissions,
    // honest label and target: the lead itself.
    if (cta.action_key === "open_lead_conversation" && isLeadsHref(href)) {
      return { label: "Abrir lead", href: href.split("#")[0] };
    }
    if (isLeadsHref(href)) return { label, href };
    if (isTasksHref(href)) return { label, href };
    // /villas, /payments, /documents are admin-only for marketing too.
    if (ADMIN_ONLY_LISTS.has(href) || isVillasHref(href)) return null;
    return null;
  }

  return null;
}
